var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React from 'jsx-dom';
import { debounce } from '@exadel/esl/modules/esl-utils/async/debounce';
import { attr, boolAttr, decorate, listen, memoize } from '@exadel/esl/modules/esl-utils/decorators';
import { UIPPluginPanel } from '../../core/panel/plugin-panel';
import { ThemeToggleIcon } from '../theme/theme-toggle.icon';
import { SettingsIcon } from './settings.icon';
/**
 * Settings {@link UIPPlugin} custom element definition
 * Container for {@link UIPSetting}
 */
export class UIPSettings extends UIPPluginPanel {
    get $icon() {
        return React.createElement(SettingsIcon, null);
    }
    get $toolbar() {
        const type = this.constructor;
        return (React.createElement("div", { class: type.is + '-toolbar uip-plugin-header-toolbar' },
            this.themeToggle ? React.createElement("uip-theme-toggle", { class: type.is + '-toolbar-option' },
                React.createElement(ThemeToggleIcon, null)) : '',
            this.dirToggle ? React.createElement("uip-dir-toggle", { class: type.is + '-toolbar-option' }) : ''));
    }
    get $inner() {
        const type = this.constructor;
        return (React.createElement("div", { class: type.is + '-inner uip-plugin-inner uip-plugin-inner-bg' },
            React.createElement("esl-scrollbar", { class: type.is + '-scrollbar', target: "::next" }),
            this.$container));
    }
    get $container() {
        const type = this.constructor;
        return (React.createElement("div", { class: `uip-plugin-content ${type.is}-container esl-scrollable-content` }));
    }
    get $items() {
        return [...this.querySelectorAll('[uip-settings-content]')];
    }
    /** @returns Element[] - active internal settings items */
    get $activeItems() {
        return this.$items.filter(($el) => !$el.classList.contains('uip-inactive-setting'));
    }
    connectedCallback() {
        super.connectedCallback();
        this.appendChild(this.$header);
        this.appendChild(this.$inner);
        this.appendChild(this.$resize);
        this.invalidate();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.append(...this.$items);
        this.removeChild(this.$header);
        this.removeChild(this.$inner);
    }
    attributeChangedCallback(attrName, oldVal, newVal) {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        if (['label', 'collapsible', 'dir-toggle', 'theme-toggle'].includes(attrName)) {
            this.$header.remove();
            this.$toolbar.remove();
            memoize.clear(this, ['$header', '$toolbar']);
            this.insertAdjacentElement('afterbegin', this.$header);
        }
    }
    invalidate() {
        memoize.clear(this, '$items');
        const outside = this.$items.filter((el) => el.parentElement !== this.$container);
        outside.forEach((el) => this.$container.appendChild(el));
        this.onSettingsStateChange();
    }
    onInvalidate() {
        this.invalidate();
    }
    /** Handles internal settings items state change */
    onSettingsStateChange() {
        this.$$attr('inactive', !this.$activeItems.length);
    }
}
UIPSettings.is = 'uip-settings';
UIPSettings.observedAttributes = ['dir-toggle', 'theme-toggle', ...UIPPluginPanel.observedAttributes];
__decorate([
    attr()
], UIPSettings.prototype, "target", void 0);
__decorate([
    boolAttr()
], UIPSettings.prototype, "dirToggle", void 0);
__decorate([
    boolAttr()
], UIPSettings.prototype, "themeToggle", void 0);
__decorate([
    boolAttr({ readonly: true })
], UIPSettings.prototype, "inactive", void 0);
__decorate([
    memoize()
], UIPSettings.prototype, "$toolbar", null);
__decorate([
    memoize()
], UIPSettings.prototype, "$inner", null);
__decorate([
    memoize()
], UIPSettings.prototype, "$container", null);
__decorate([
    memoize()
    /** @returns HTMLElement[] - all internal items marked as settings item */
], UIPSettings.prototype, "$items", null);
__decorate([
    decorate(debounce, 100)
], UIPSettings.prototype, "invalidate", null);
__decorate([
    listen('uip:settings:invalidate')
], UIPSettings.prototype, "onInvalidate", null);
__decorate([
    listen('uip:settings:state:change')
], UIPSettings.prototype, "onSettingsStateChange", null);
